import { API_CONFIG } from '@/config';

interface ApiOptions extends RequestInit {
  params?: Record<string, string>;
}

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
    this.defaultHeaders = {
      'Accept': 'application/json'
    };
  }

  private getAuthHeader(): HeadersInit {
    const sessionId = localStorage.getItem('sessionId');
    return sessionId ? { 'Authorization': `Bearer ${sessionId}` } : {};
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json();
    
    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Request failed');
    }

    return data.data;
  }

  async get<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = new URL(cleanEndpoint, this.baseUrl);
    
    if (options.params) {
      Object.entries(options.params).forEach(([key, value]) => {
        url.searchParams.append(key, value);
      });
    }

    const response = await fetch(url.toString(), {
      ...options,
      headers: {
        ...this.defaultHeaders,
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
        ...options.headers
      }
    });

    return this.handleResponse<T>(response);
  }

  async post<T>(endpoint: string, data?: unknown, options: ApiOptions = {}): Promise<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = new URL(cleanEndpoint, this.baseUrl);

    // Check if data is FormData
    const isFormData = data instanceof FormData;
    
    const response = await fetch(url.toString(), {
      ...options,
      method: 'POST',
      headers: {
        ...(!isFormData && { 'Content-Type': 'application/json' }),
        ...this.defaultHeaders,
        ...this.getAuthHeader(),
        ...options.headers
      },
      body: isFormData ? data : data ? JSON.stringify(data) : undefined
    });

    return this.handleResponse<T>(response);
  }

  async delete<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = new URL(cleanEndpoint, this.baseUrl);
    
    const response = await fetch(url.toString(), {
      ...options,
      method: 'DELETE',
      headers: {
        ...this.defaultHeaders,
        'Content-Type': 'application/json',
        ...this.getAuthHeader(),
        ...options.headers
      }
    });

    return this.handleResponse<T>(response);
  }
}

export const api = new ApiClient();